import type { CampaignTerminalsResponse } from "components/Form/formComponents/TerminalToCampaignTable/types";
import i18n from "i18next";
import { type Dispatch, type FC, type SetStateAction, useEffect, useState } from "react";
import styled from "styled-components";

import { CAMPAIGN_STATE } from "src/app/features/Campaign/types/CampaignState";
import type { TerminalToCampaignEntity } from "src/app/features/Campaign/types/TerminalToCampaignEntity";
import { Palette } from "../../core/styles/palette";

export interface Segment {
    percentage: number;
    count: number;
    color: string;
    label: string;
}

interface MultiSegmentProgressBarProps {
    value?: TerminalToCampaignEntity[];
    data?: CampaignTerminalsResponse;
    setTerminals: Dispatch<SetStateAction<TerminalToCampaignEntity[]>>;
    refreshIndex: number;
}

export const MultiSegmentProgressBar: FC<MultiSegmentProgressBarProps> = ({ value, data, setTerminals, refreshIndex }) => {
    const [segments, setSegments] = useState<Segment[]>([]);

    useEffect(() => {
        if (!value?.length || !data?.items) {
            setSegments([]);
            return;
        }

        const phaseTerminalIds = new Set(value.map((terminal) => terminal.terminalSystemId));
        const phaseTerminals = data.items.filter((terminal) => phaseTerminalIds.has(terminal.terminalSystemId));
        setTerminals(phaseTerminals);

        const total = phaseTerminals.length;
        const updated = phaseTerminals.filter((item) => item.updateStatus === CAMPAIGN_STATE.UPDATED);
        const failed = phaseTerminals.filter((item) => item.updateStatus === CAMPAIGN_STATE.FAILED);
        const unfinished = phaseTerminals.filter(
            (item) => item.updateStatus !== CAMPAIGN_STATE.FAILED && item.updateStatus !== CAMPAIGN_STATE.UPDATED,
        );

        setSegments([
            {
                percentage: total === 0 ? 0 : (updated.length / total) * 100,
                count: updated.length,
                color: Palette.HeroGreen,
                label: i18n.t("campaignCreate.phase.progress.updated"),
            },
            {
                percentage: total === 0 ? 0 : (unfinished.length / total) * 100,
                count: unfinished.length,
                color: Palette.Warning,
                label: i18n.t("campaignCreate.phase.progress.unfinished"),
            },
            {
                percentage: total === 0 ? 0 : (failed.length / total) * 100,
                count: failed.length,
                color: Palette.AeviRed,
                label: i18n.t("campaignCreate.phase.progress.failed"),
            },
        ]);
    }, [value, data, setTerminals, refreshIndex]);

    const visibleSegments = segments.filter((seg) => seg.count > 0);
    const formatSegment = (seg: Segment) => `${seg.label} ${seg.percentage.toFixed(2)}% (${seg.count})`;

    return (
        <div style={{ marginTop: 16 }}>
            <BarContainerStyled>
                {visibleSegments.map((seg) => (
                    <ProgressBarStyled
                        state={seg}
                        key={`${seg.label}-${seg.color}`}
                        title={formatSegment(seg)}
                        aria-label={formatSegment(seg)}
                        aria-valuenow={seg.percentage}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        role="progressbar"
                    />
                ))}
            </BarContainerStyled>
            {visibleSegments.length > 0 && (
                <LegendStyled>
                    {visibleSegments.map((seg) => (
                        <LegendItemStyled key={`${seg.label}-${seg.color}`}>
                            <LegendSwatchStyled color={seg.color} />
                            {formatSegment(seg)}
                        </LegendItemStyled>
                    ))}
                </LegendStyled>
            )}
        </div>
    );
};

const BarContainerStyled = styled.div`
    display: flex;
    height: 24px;
    border-radius: 20px;
    overflow: hidden;
    background: ${Palette.DisabledBackground};
`;

// Segment labels live in the legend rather than inside the bar: a segment's width is proportional to its share,
// so a small share (e.g. 4 updated out of 250) is too narrow to hold readable text.
const ProgressBarStyled = styled.div<{ state: Segment }>`
    width: ${(props) => props.state.percentage}%;
    min-width: 8px;
    background: ${(props) => props.state.color};
`;

const LegendStyled = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 4px 16px;
    margin-top: 8px;
    font-size: 12px;
    font-weight: 500;
`;

const LegendItemStyled = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 6px;
`;

const LegendSwatchStyled = styled.span<{ color: string }>`
    width: 10px;
    height: 10px;
    border-radius: 50%;
    background: ${(props) => props.color};
`;
